import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useQuery } from 'react-query';
import { FaPrint, FaHistory } from 'react-icons/fa';

import API from 'core/Api';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { getSubdomain } from 'utils/helpers/routing';

import { ISelectOptionProps } from 'components/form/types';
import FetchTable from 'components/table/FetchTable';
import { buildFilters } from 'components/table/utils/filtersConfig';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import Spinner from 'components/Spinner';

import InnerWrapper from '../InnerWrapper';

const CompletedScreen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const history = useHistory();
  const isOperator = getSubdomain() === 'operators';

  const [customSelectableRowProps, setCustomSelectableRowProps] = useState<any[]>([]);
  const [multiActionActive, setMultiActionActive] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const createGroup = () => {
    const url = buildClientUrl('/health-and-safety-training/create', client);
    history.push(url);
  };

  const renderActions = (rowData: any) => (
    <>
      <span className="d-inline-block ml-2 mt-2">
        <TooltipWrapper
          id="tooltip-print"
          overlay={t('documents.table.preview')}
        >
          <Button
            type="button"
            btnType="dark"
            size="sm"
            outlined
            disabled={isLoadingDownload}
            onClick={() => handlePreviewDoc({ showModal, id: rowData.document_id, setIsLoading: setIsLoadingDownload })}
          >
            {!isLoadingDownload && <FaPrint size={14} />}
            {isLoadingDownload && <Spinner size={14} />}
          </Button>
        </TooltipWrapper>
      </span>

      <div className="d-inline-block ml-2 mt-2">
        <TooltipWrapper
          id="tooltip-history"
          overlay={t('documents.table.history')}
        >
          <Button
            type="button"
            btnType="dark"
            size="sm"
            outlined
            onClick={() => history.push(buildClientUrl(`/document-activity/${rowData.document_id}`, client))}
          >
            <FaHistory size={14} />
          </Button>
        </TooltipWrapper>
      </div>
    </>
  );

  const columns: Array<Column<any>> = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: t('healthAndSafetyTraining.user'),
      accessor: 'user',
      Cell: ({ row }) => `${row.original.user.first_name} ${row.original.user.last_name}`,
    },
    {
      Header: t('healthAndSafetyTraining.group'),
      accessor: 'user.groups',
      Cell: ({ row }) => row.original.user.groups?.map((g: any) => g.name).join(', ') || '',
    },
    {
      Header: t('healthAndSafetyTraining.type'),
      accessor: 'type',
      width: '5rem',
      Cell: ({ row }) => t(`healthAndSafetyTraining.types.${row.original.type}`),
    },
    {
      Header: t('healthAndSafetyTraining.documentNumber'),
      accessor: 'document_id',
      width: '5rem',
      Cell: ({ row }) => row.original.document_id || '',
    },
    {
      Header: t('healthAndSafetyTraining.date'),
      accessor: 'facilitated_on',
      width: '5rem',
      Cell: ({ row }) =>
        row.original.facilitated_on ? formatDate(new Date(row.original.facilitated_on), 'dd.mm.YYY') : '',
    },
    {
      Header: t('healthAndSafetyTraining.status'),
      accessor: 'status',
      width: '5rem',
      Cell: ({ row }) => t(`healthAndSafetyTraining.statuses.${row.original.status}`),
    },
    {
      Header: t('healthAndSafetyTraining.actions'),
      accessor: 'actions',
      width: '5rem',
      Cell: ({ row }) => <div style={{ textAlign: 'right' }}>{renderActions(row.original)}</div>,
    },
  ];

  const {
    isFetching,
    isLoading,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('health-and-safety-training-completed-dropdowns', client)],
    async () => {
      const res = await API.get('health-and-safety-training/dropdown-options');
      return res.data?.[language || 'bg'];
    },
    { cacheTime: 0 }
  );

  const tableFilters = useMemo(() => {
    if (!dropdowns) return null;

    const statusWithDefaults = dropdowns.status?.map(opt => {
      if (opt.value === 'completed' || opt.value === 'rejected') {
        return { ...opt, selected: true };
      }
      return opt;
    });

    return [
      buildFilters({ name: 'facilitated_on_gte', placeholder: t('leaves.dateFrom'), type: 'monthyearpickerstart' }),
      buildFilters({
        name: 'facilitated_on_lte',
        placeholder: t('leaves.dateTo'),
        type: 'monthyearpickerend',
        subType: 'healthAndSafetyCompleted',
        setLastMonth: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('healthAndSafetyTraining.user'),
        options: dropdowns.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'type',
        placeholder: t('healthAndSafetyTraining.type'),
        options: dropdowns.type,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'facilitator',
        placeholder: t('healthAndSafetyTraining.facilitator'),
        options: dropdowns.facilitator,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'status',
        placeholder: t('healthAndSafetyTraining.status'),
        options: statusWithDefaults,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [dropdowns, t]);

  return (
    <InnerWrapper
      canMultiDownload
      customSelectableProps={customSelectableRowProps}
      setMultiActionActive={setMultiActionActive}
      setCustomSelectableRowProps={setCustomSelectableRowProps}
    >
      {tableFilters && (
        <FetchTable
          loading={isFetching || isLoading}
          entityName={buildClientEntity('health-and-safety-training-completed', client)}
          entityEndpoint="/health-and-safety-training"
          columns={columns}
          filters={tableFilters}
          pageLimit={20}
          pagination
          hideSearch
          isSelectable
          useCustomSelectBtn
          customSelectableRowProps={customSelectableRowProps}
          disablePagination={multiActionActive}
          {...(!isOperator && {
            onAdd: createGroup,
            addBtnLabel: t('healthAndSafetyTraining.create'),
          })}
        />
      )}
    </InnerWrapper>
  );
};

export default CompletedScreen;
